import { useState, useEffect, useRef } from 'react'
import { TagInput } from './TagInput'
import { QualityIndicator } from './QualityIndicator'
import { apiPost } from '../../lib/auth'
import { useApi, asArray } from '../../hooks/useApi'
import { Sparkles, Plus, X, Loader2, HelpCircle, ChevronDown, ChevronUp, Check, Search } from 'lucide-react'

interface CampaignFormProps {
  clientId: number
  onSuccess: (campaign: any) => void
  onCancel: () => void
}

const inputClass = 'w-full px-3 py-2 border border-[#E2E4ED] rounded-lg text-sm text-[#1B2559] focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1]/20 outline-none'
const labelClass = 'block text-sm font-medium text-[#1B2559] mb-1'
const btnPrimary = 'px-4 py-2 bg-[#6366F1] text-white rounded-lg hover:bg-[#5558E6] transition-colors text-sm font-medium'
const btnSecondary = 'px-4 py-2 border border-[#E2E4ED] text-[#555A73] rounded-lg hover:bg-[#F4F5FA] transition-colors text-sm'

const SEARCH_INTENTS = ['Trouver un prestataire', 'Comparer', "S'informer", 'Acheter']

const CATEGORY_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  A: { label: 'Recherche directe', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
  B: { label: 'Comparaison', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
  C: { label: 'Informationnelle', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
}

const TOOLTIP_TEXTS: Record<string, string> = {
  businessType: "D\u00e9termine le type de requ\u00eates g\u00e9n\u00e9r\u00e9es : B2B produit des questions professionnelles, Local ajoute des requ\u00eates g\u00e9olocalis\u00e9es, E-commerce oriente vers l'achat. Institution / F\u00e9d\u00e9ration : les requ\u00eates cherchent \u00e0 faire citer des organisations, syndicats ou f\u00e9d\u00e9rations (pas des entreprises) \u2014 pour une entit\u00e9 comme la FNH.",
  targetPersona: "Le profil de l'utilisateur qui interroge un LLM. Un dirigeant PME posera des questions diff\u00e9rentes d'un responsable marketing.",
  searchIntents: "Les intentions derri\u00e8re les requ\u00eates. Cocher plusieurs options g\u00e9n\u00e8re un mix plus r\u00e9aliste de requ\u00eates.",
  maturityLevel: "D\u00e9couverte = requ\u00eates g\u00e9n\u00e9riques, Consid\u00e9ration = comparatifs, D\u00e9cision = requ\u00eates avec intention d'achat.",
  knownKeywords: "Vos mots-cl\u00e9s SEO actuels. L'IA s'en inspire pour g\u00e9n\u00e9rer des requ\u00eates au style similaire mais adapt\u00e9es aux LLMs.",
  additionalContext: "Pr\u00e9cisez votre positionnement, vos USPs ou votre march\u00e9 pour des requ\u00eates plus cibl\u00e9es.",
}

// --- Tooltip Component ---
function Tooltip({ text, children }: { text: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  return (
    <span className="relative inline-flex items-center ml-1">
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </span>
      {show && (
        <div
          ref={ref}
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-64 px-3 py-2 text-xs text-white bg-[#1B2559] rounded-lg shadow-lg pointer-events-none"
        >
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-[#1B2559] rotate-45 -mt-1" />
        </div>
      )}
    </span>
  )
}

// --- Keyword Suggestion from Rankings ---
interface RankingKeyword {
  id: number
  keyword: string
  position: number | null
  volume: number | null
}

function KeywordSuggestions({
  clientId,
  selected,
  onAdd,
  onRemove,
}: {
  clientId: number
  selected: string[]
  onAdd: (kw: string) => void
  onRemove: (kw: string) => void
}) {
  const { data } = useApi<any>(clientId ? `/api/keywords/clients/${clientId}` : '')
  const [filter, setFilter] = useState('')
  const [expanded, setExpanded] = useState(false)

  const keywords: RankingKeyword[] = (data as any)?.keywords
    ? (data as any).keywords
        .filter((k: any) => k.position != null && k.position <= 30)
        .sort((a: any, b: any) => (a.position ?? 999) - (b.position ?? 999))
        .slice(0, 50)
    : []

  if (!keywords.length) return null

  const filtered = filter
    ? keywords.filter(k => k.keyword.toLowerCase().includes(filter.toLowerCase()))
    : keywords

  const displayCount = expanded ? filtered.length : Math.min(filtered.length, 10)

  return (
    <div className="mt-2 border border-[#E2E4ED] rounded-lg bg-[#FAFBFE] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E4ED]">
        <span className="text-xs font-medium text-[#555A73]">
          Suggestions depuis le module Classement ({keywords.length} mots-cl&eacute;s)
        </span>
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8B8FA7]" />
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filtrer..."
            className="pl-7 pr-2 py-1 text-xs border border-[#E2E4ED] rounded-md w-36 outline-none focus:border-[#6366F1]"
          />
        </div>
      </div>
      <div className="max-h-48 overflow-y-auto">
        {filtered.slice(0, displayCount).map(kw => {
          const isSelected = selected.includes(kw.keyword)
          return (
            <button
              key={kw.id}
              type="button"
              onClick={() => isSelected ? onRemove(kw.keyword) : onAdd(kw.keyword)}
              className={`w-full flex items-center justify-between px-3 py-1.5 text-xs hover:bg-[#F4F5FA] transition-colors ${
                isSelected ? 'bg-[#6366F1]/5' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded flex items-center justify-center border ${
                  isSelected
                    ? 'bg-[#6366F1] border-[#6366F1] text-white'
                    : 'border-[#E2E4ED]'
                }`}>
                  {isSelected && <Check size={10} />}
                </div>
                <span className={`${isSelected ? 'text-[#6366F1] font-medium' : 'text-[#1B2559]'}`}>
                  {kw.keyword}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[#8B8FA7]">
                {kw.position && (
                  <span className="tabular-nums">#{kw.position}</span>
                )}
                {kw.volume != null && (
                  <span className="tabular-nums">{kw.volume.toLocaleString()} vol.</span>
                )}
              </div>
            </button>
          )
        })}
      </div>
      {filtered.length > 10 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full py-1.5 text-xs text-[#6366F1] hover:bg-[#F4F5FA] border-t border-[#E2E4ED]"
        >
          {expanded ? 'Voir moins' : `Voir les ${filtered.length - 10} autres`}
        </button>
      )}
    </div>
  )
}

export function CampaignForm({ clientId, onSuccess, onCancel }: CampaignFormProps) {
  const [name, setName] = useState('')
  const [brand, setBrand] = useState('')
  const [brandVariants, setBrandVariants] = useState<string[]>([])
  const [competitors, setCompetitors] = useState<string[]>([])
  const [sector, setSector] = useState('')
  const [locations, setLocations] = useState<string[]>([])
  const [runsPerQuery, setRunsPerQuery] = useState(3)
  const [queries, setQueries] = useState<{ text: string; category: 'A' | 'B' | 'C' }[]>([])

  // Context fields
  const [businessType, setBusinessType] = useState('')
  const [targetPersona, setTargetPersona] = useState('')
  const [searchIntents, setSearchIntents] = useState<string[]>([])
  const [maturityLevel, setMaturityLevel] = useState('')
  const [knownKeywords, setKnownKeywords] = useState<string[]>([])
  const [additionalContext, setAdditionalContext] = useState('')

  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [contextOpen, setContextOpen] = useState(false)

  const filledCount = [
    businessType,
    locations.length > 0,
    (competitors.length > 0 || sector),
    targetPersona,
    searchIntents.length > 0,
    maturityLevel,
    knownKeywords.length > 0,
    additionalContext,
  ].filter(Boolean).length

  const toggleIntent = (intent: string) => {
    setSearchIntents(prev =>
      prev.includes(intent) ? prev.filter(i => i !== intent) : [...prev, intent]
    )
  }

  const handleAddKeyword = (kw: string) => {
    if (!knownKeywords.includes(kw)) {
      setKnownKeywords(prev => [...prev, kw])
    }
  }

  const handleRemoveKeyword = (kw: string) => {
    setKnownKeywords(prev => prev.filter(k => k !== kw))
  }

  const handleGenerateQueries = async () => {
    if (!brand.trim()) {
      setError('La marque est requise pour g\u00e9n\u00e9rer des requ\u00eates.')
      return
    }
    setGenerating(true)
    setError('')
    try {
      const res = await apiPost('/api/llm/generate-queries', {
        brand,
        contextFields: {
          brandVariants,
          competitors,
          sector,
          locations,
          businessType,
          targetPersona,
          searchIntents,
          maturityLevel,
          knownKeywords,
          additionalContext,
        },
        count: 7,
      })
      if (res.error) {
        setError(res.error)
      } else if (res.data) {
        const d = res.data as any
        const rawQueries = d.queries || d.data?.queries || []
        setQueries(rawQueries.map((q: any) => ({
          text: q.text || q,
          category: q.category || 'A',
        })))
      }
    } catch {
      setError('Erreur lors de la g\u00e9n\u00e9ration des requ\u00eates.')
    } finally {
      setGenerating(false)
    }
  }

  const updateQuery = (index: number, field: 'text' | 'category', value: string) => {
    setQueries(prev => prev.map((q, i) =>
      i === index ? { ...q, [field]: value } : q
    ))
  }

  const removeQuery = (index: number) => {
    setQueries(prev => prev.filter((_, i) => i !== index))
  }

  const addQuery = () => {
    setQueries(prev => [...prev, { text: '', category: 'A' }])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !brand.trim()) {
      setError('Le nom et la marque sont requis.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const res = await apiPost('/api/llm/campaigns', {
        clientId,
        name,
        brand,
        brandVariants,
        competitors,
        sector,
        locations,
        runsPerQuery,
        queries,
        contextFields: {
          businessType,
          targetPersona,
          searchIntents,
          maturityLevel,
          knownKeywords,
          additionalContext,
        },
      })
      if (res.error) {
        setError(res.error)
      } else if (res.data) {
        onSuccess(res.data)
      }
    } catch {
      setError('Erreur lors de la cr\u00e9ation de la campagne.')
    } finally {
      setSaving(false)
    }
  }

  // Group queries by category for display
  const queriesByCategory = queries.reduce((acc, q, originalIndex) => {
    if (!acc[q.category]) acc[q.category] = []
    acc[q.category].push({ ...q, originalIndex })
    return acc
  }, {} as Record<string, (typeof queries[0] & { originalIndex: number })[]>)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Section 1 - Basic Info */}
      <div className="bg-white border border-[#E2E4ED] rounded-lg p-5 space-y-4">
        <h3 className="text-base font-semibold text-[#1B2559]">Informations de base</h3>

        <div>
          <label className={labelClass}>Nom de la campagne *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ex: Audit visibilit&eacute; LLM Q2 2026"
            className={inputClass}
            required
          />
        </div>

        <div>
          <label className={labelClass}>Marque *</label>
          <input
            type="text"
            value={brand}
            onChange={e => setBrand(e.target.value)}
            placeholder="Ex: Elorion"
            className={inputClass}
            required
          />
        </div>

        <div>
          <label className={labelClass}>Variantes de marque</label>
          <TagInput
            value={brandVariants}
            onChange={setBrandVariants}
            placeholder="Ex: elorion.fr, agence Elorion..."
          />
        </div>

        <div>
          <label className={labelClass}>Concurrents</label>
          <TagInput
            value={competitors}
            onChange={setCompetitors}
            placeholder="Ex: SEMrush, Ahrefs..."
          />
        </div>

        <div>
          <label className={labelClass}>Secteur</label>
          <input
            type="text"
            value={sector}
            onChange={e => setSector(e.target.value)}
            placeholder="Ex: Marketing digital"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Zones g&eacute;ographiques</label>
          <TagInput
            value={locations}
            onChange={setLocations}
            placeholder="Ex: France, Alsace, Mulhouse..."
          />
        </div>
      </div>

      {/* Section 2 - Context Fields (collapsible) */}
      <div className="bg-white border border-[#E2E4ED] rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setContextOpen(!contextOpen)}
          className="w-full flex items-center justify-between p-5 hover:bg-[#F4F5FA]/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-[#1B2559]">Champs guid&eacute;s</h3>
            {filledCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#6366F1]/10 text-[#6366F1] font-medium">
                {filledCount}/8 remplis
              </span>
            )}
          </div>
          {contextOpen ? <ChevronUp size={18} className="text-[#555A73]" /> : <ChevronDown size={18} className="text-[#555A73]" />}
        </button>

        {contextOpen && (
          <div className="px-5 pb-5 space-y-4 border-t border-[#E2E4ED]">
            <p className="text-xs text-[#8B8FA7] pt-3">
              Ces champs aident l'IA &agrave; g&eacute;n&eacute;rer des requ&ecirc;tes plus pertinentes et r&eacute;alistes.
            </p>

            <div>
              <label className={labelClass}>
                Type de business
                <Tooltip text={TOOLTIP_TEXTS.businessType}>
                  <HelpCircle size={14} className="text-[#8B8FA7]" />
                </Tooltip>
              </label>
              <select
                value={businessType}
                onChange={e => setBusinessType(e.target.value)}
                className={inputClass}
              >
                <option value="">-- S&eacute;lectionner --</option>
                <option value="B2B">B2B</option>
                <option value="B2C">B2C</option>
                <option value="Local">Local</option>
                <option value="E-commerce">E-commerce</option>
                <option value="Institution">Institution / Fédération</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Persona cible
                <Tooltip text={TOOLTIP_TEXTS.targetPersona}>
                  <HelpCircle size={14} className="text-[#8B8FA7]" />
                </Tooltip>
              </label>
              <select
                value={targetPersona}
                onChange={e => setTargetPersona(e.target.value)}
                className={inputClass}
              >
                <option value="">-- S&eacute;lectionner --</option>
                <option value="Dirigeant PME">Dirigeant PME</option>
                <option value="Responsable marketing">Responsable marketing</option>
                <option value="Particulier">Particulier</option>
                <option value="Etudiant">&Eacute;tudiant</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Intentions de recherche
                <Tooltip text={TOOLTIP_TEXTS.searchIntents}>
                  <HelpCircle size={14} className="text-[#8B8FA7]" />
                </Tooltip>
              </label>
              <div className="flex flex-wrap gap-2 mt-1">
                {SEARCH_INTENTS.map(intent => (
                  <label
                    key={intent}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-colors ${
                      searchIntents.includes(intent)
                        ? 'border-[#6366F1] bg-[#6366F1]/10 text-[#6366F1]'
                        : 'border-[#E2E4ED] text-[#555A73] hover:bg-[#F4F5FA]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={searchIntents.includes(intent)}
                      onChange={() => toggleIntent(intent)}
                      className="sr-only"
                    />
                    {intent}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className={labelClass}>
                Niveau de maturit&eacute;
                <Tooltip text={TOOLTIP_TEXTS.maturityLevel}>
                  <HelpCircle size={14} className="text-[#8B8FA7]" />
                </Tooltip>
              </label>
              <select
                value={maturityLevel}
                onChange={e => setMaturityLevel(e.target.value)}
                className={inputClass}
              >
                <option value="">-- S&eacute;lectionner --</option>
                <option value="Decouverte">D&eacute;couverte</option>
                <option value="Consideration">Consid&eacute;ration</option>
                <option value="Decision">D&eacute;cision</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Mots-cl&eacute;s r&eacute;els connus
                <Tooltip text={TOOLTIP_TEXTS.knownKeywords}>
                  <HelpCircle size={14} className="text-[#8B8FA7]" />
                </Tooltip>
              </label>
              <TagInput
                value={knownKeywords}
                onChange={setKnownKeywords}
                placeholder="Ex: agence seo mulhouse, audit seo..."
              />
              <KeywordSuggestions
                clientId={clientId}
                selected={knownKeywords}
                onAdd={handleAddKeyword}
                onRemove={handleRemoveKeyword}
              />
            </div>

            <div>
              <label className={labelClass}>
                Contexte suppl&eacute;mentaire
                <Tooltip text={TOOLTIP_TEXTS.additionalContext}>
                  <HelpCircle size={14} className="text-[#8B8FA7]" />
                </Tooltip>
              </label>
              <textarea
                value={additionalContext}
                onChange={e => setAdditionalContext(e.target.value)}
                placeholder="Ex: Nous sommes sp&eacute;cialis&eacute;s dans le SEO local pour artisans en Alsace..."
                rows={3}
                className={inputClass + ' resize-none'}
              />
            </div>
          </div>
        )}
      </div>

      {/* Section 3 - Quality Indicator */}
      <QualityIndicator filledCount={filledCount} />

      {/* Section 4 - Query Generation */}
      <div className="bg-white border border-[#E2E4ED] rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-[#1B2559]">Requ&ecirc;tes</h3>
          {queries.length > 0 && (
            <span className="text-xs text-[#8B8FA7]">{queries.length} requ&ecirc;te{queries.length > 1 ? 's' : ''}</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleGenerateQueries}
            disabled={generating || !brand.trim()}
            className={`${btnPrimary} inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {generating ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Sparkles size={16} />
            )}
            {generating ? 'G\u00e9n\u00e9ration en cours...' : 'G\u00e9n\u00e9rer les requ\u00eates'}
          </button>
          {queries.length > 0 && (
            <button
              type="button"
              onClick={addQuery}
              className={`${btnSecondary} inline-flex items-center gap-1.5`}
            >
              <Plus size={14} />
              Ajouter manuellement
            </button>
          )}
        </div>

        {/* Queries grouped by category */}
        {queries.length > 0 && (
          <div className="space-y-4">
            {(['A', 'B', 'C'] as const).map(cat => {
              const catQueries = queriesByCategory[cat]
              if (!catQueries?.length) return null
              const { label, color, bg } = CATEGORY_LABELS[cat]
              return (
                <div key={cat} className={`border rounded-lg overflow-hidden ${bg}`}>
                  <div className={`px-3 py-2 flex items-center gap-2 ${color}`}>
                    <span className="text-xs font-bold uppercase tracking-wider">{cat}</span>
                    <span className="text-xs font-medium">{label}</span>
                    <span className="text-xs opacity-60">({catQueries.length})</span>
                  </div>
                  <div className="bg-white divide-y divide-[#E2E4ED]">
                    {catQueries.map(({ originalIndex, text }) => (
                      <div key={originalIndex} className="flex items-center gap-2 px-3 py-2">
                        <input
                          type="text"
                          value={text}
                          onChange={e => updateQuery(originalIndex, 'text', e.target.value)}
                          placeholder="Requ\u00eate..."
                          className="flex-1 outline-none text-sm text-[#1B2559] bg-transparent"
                        />
                        <select
                          value={cat}
                          onChange={e => updateQuery(originalIndex, 'category', e.target.value)}
                          className="px-2 py-1 border border-[#E2E4ED] rounded text-xs text-[#555A73] outline-none focus:border-[#6366F1]"
                        >
                          <option value="A">A - Directe</option>
                          <option value="B">B - Comparaison</option>
                          <option value="C">C - Info</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => removeQuery(originalIndex)}
                          className="p-1 text-[#8B8FA7] hover:text-red-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div>
          <label className={labelClass}>Runs par requ&ecirc;te</label>
          <input
            type="number"
            value={runsPerQuery}
            onChange={e => setRunsPerQuery(Math.max(1, Math.min(10, Number(e.target.value))))}
            min={1}
            max={10}
            className={inputClass + ' w-24'}
          />
          <p className="text-xs text-[#8B8FA7] mt-1">Nombre de fois que chaque requ&ecirc;te sera envoy&eacute;e au LLM (1-10)</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Section 5 - Submit */}
      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className={btnSecondary}>
          Annuler
        </button>
        <button
          type="submit"
          disabled={saving || !name.trim() || !brand.trim()}
          className={`${btnPrimary} inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          Cr&eacute;er la campagne
        </button>
      </div>
    </form>
  )
}
